import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { CalendarCheck, ArrowRight, ShieldCheck, Star, TrendUp } from "@phosphor-icons/react";
import { AnimatedCounter } from "@/components/site/Primitives";
import { TOOLS } from "@/data/content";

export const Hero = ({ onBook }) => {
  const container = { hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } } };
  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
  };

  return (
    <section id="home" data-testid="hero-section" className="relative overflow-hidden jp-hero-grid pt-32 md:pt-44 pb-24 md:pb-32">
      {/* Aurora blobs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 w-[32rem] h-[32rem] rounded-full bg-gold/20 blur-3xl animate-blob" />
        <div className="absolute top-20 -right-24 w-[30rem] h-[30rem] rounded-full bg-navy/15 blur-3xl animate-blob animation-delay-2000" />
        <div className="absolute -bottom-32 left-1/3 w-[28rem] h-[28rem] rounded-full bg-gold/10 blur-3xl animate-blob animation-delay-4000" />
      </div>

      <div className="relative w-full max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-12 items-center">
          <motion.div variants={container} initial="hidden" animate="show">
            <motion.div variants={item} className="inline-flex items-center gap-2 rounded-full bg-white/70 backdrop-blur border border-navy/10 px-4 py-1.5 shadow-sm">
              <ShieldCheck size={16} weight="fill" className="text-gold" />
              <span className="text-xs font-semibold uppercase tracking-widest text-navy">Ahmedabad · Trusted Since Day One</span>
            </motion.div>

            <motion.h1 variants={item} className="mt-6 font-display text-4xl sm:text-5xl lg:text-[4.2rem] font-bold tracking-tight leading-[1.05]">
              <span className="text-navy">Professional Accounting & Taxation for </span>
              <span className="text-gradient-gold italic">Individuals & Businesses</span>
            </motion.h1>

            <motion.p variants={item} className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-xl">
              Simplifying accounting, GST, TDS, Income Tax Return (ITR) filing, and bookkeeping with accurate,
              timely, and reliable financial solutions.
            </motion.p>

            <motion.div variants={item} className="mt-9 flex flex-col sm:flex-row gap-4">
              <Button onClick={onBook} data-testid="hero-book-consultation"
                className="btn-shine rounded-full bg-navy hover:bg-navy-hover text-white font-semibold px-8 h-14 text-base transition-all duration-300 hover:shadow-xl hover:shadow-navy/20 group">
                <CalendarCheck size={20} weight="fill" className="mr-2" />
                Book Free Consultation
                <ArrowRight size={18} className="ml-1 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button asChild variant="outline" data-testid="hero-contact-us"
                className="rounded-full border-navy/30 text-navy hover:bg-navy hover:text-white font-semibold px-8 h-14 text-base transition-all duration-300">
                <a href="#contact">Contact Us</a>
              </Button>
            </motion.div>

            <motion.div variants={item} className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3">
              <div className="flex items-center gap-1.5">
                {[...Array(5)].map((_, i) => (
                  <motion.span key={i} initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 1 + i * 0.08, type: "spring", stiffness: 300 }}>
                    <Star size={16} weight="fill" className="text-gold" />
                  </motion.span>
                ))}
                <span className="text-sm text-slate-600 ml-1">Reliable & confidential</span>
              </div>
              <div className="hidden sm:block h-4 w-px bg-slate-300" />
              <span className="text-sm text-slate-500">{TOOLS.join(" · ")}</span>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94, rotate: -1 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.div
              animate={{ rotate: [3, 5, 3] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -inset-4 bg-gold/15 rounded-[2.2rem]"
            />
            <div className="relative rounded-[1.9rem] overflow-hidden shadow-2xl border-4 border-white">
              <img
                data-testid="hero-image"
                src="https://images.pexels.com/photos/12437056/pexels-photo-12437056.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Financial professional at work"
                className="w-full h-[420px] md:h-[540px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/25 via-transparent to-transparent" />
            </div>

            <div
              className="absolute -bottom-6 -left-4 md:left-6 bg-white/95 backdrop-blur rounded-2xl shadow-xl border border-slate-100 px-6 py-4 animate-float z-10"
            >
              <p className="font-display text-3xl font-bold text-navy">
                <AnimatedCounter to={100} suffix="%" />
              </p>
              <p className="text-sm text-slate-500 font-medium">Compliance Focused</p>
            </div>

            <div
              className="absolute -top-5 right-2 md:-right-5 bg-navy text-white rounded-2xl shadow-xl px-5 py-4 flex items-center gap-3 z-10"
            >
              <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center text-gold">
                <TrendUp size={22} weight="bold" />
              </div>
              <div>
                <p className="font-display text-xl font-bold leading-none">
                  <AnimatedCounter to={3} suffix="+" />
                </p>
                <p className="text-xs text-white/70">Years Experience</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
